/**
 * Thin wrapper around useDocWorkflow that adds toast notifications,
 * navigation for amend, and re-hydration signaling.
 *
 * Extracted from StandardDetailPage to keep it under the 800-line limit.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useDocWorkflow, extractErrorMessage } from "@/hooks/useDocWorkflow";
import { buildDetailRoute } from "@/lib/config/docRoutes";
import type { UseDocWorkflowOptions } from "@/hooks/useDocWorkflow";

interface UseDocWorkflowHandlersOptions extends UseDocWorkflowOptions {
  /** Called after successful submit/cancel to allow re-hydration from server data */
  onMutationSuccess?: () => void;
}

export function useDocWorkflowHandlers(options: UseDocWorkflowHandlersOptions) {
  const { doctype, onMutationSuccess } = options;
  const navigate = useNavigate();
  const workflow = useDocWorkflow(options);

  const handleDocSubmit = useCallback(async () => {
    try {
      await workflow.submitDoc();
      onMutationSuccess?.();
      toast.success(`${doctype} submitted successfully`);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }, [workflow.submitDoc, doctype, onMutationSuccess]);

  const handleDocCancel = useCallback(async () => {
    try {
      await workflow.cancelDoc();
      onMutationSuccess?.();
      toast.success(`${doctype} cancelled`);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }, [workflow.cancelDoc, doctype, onMutationSuccess]);

  const handleDocAmend = useCallback(async () => {
    try {
      const result = await workflow.amendDoc();
      const newDoc = result?.message || result;
      if (newDoc?.name) {
        toast.info("Amendment created — review and submit when ready");
        navigate(buildDetailRoute(doctype, newDoc.name));
      }
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }, [workflow.amendDoc, doctype, navigate]);

  return {
    ...workflow,
    handleDocSubmit,
    handleDocCancel,
    handleDocAmend,
  };
}
